import type { CSSProperties, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'

const STATEMENTS: Array<{ text: string, lines: Array<ReactNode> }> = [
    {
        text: "Everything here touches fire.",
        lines: ["Everything here", <>touches <em>fire</em>.</>]
    },
    {
        text: "Technique is the quiet part.",
        lines: ["Technique is", <>the <em>quiet</em> part.</>]
    },
    {
        text: "Nothing is rushed. Nothing is wasted.",
        lines: [<>Nothing is <em>rushed</em>.</>, <>Nothing is <em>wasted</em>.</>]
    }
]

const Philosophy = (): ReactNode => {
    const section = useRef<HTMLElement>(null)
    const bar = useRef<HTMLSpanElement>(null)
    const [active, setActive] = useState(0)
    const [started, setStarted] = useState(false)

    useEffect(() => {
        const element = section.current

        if (!element) return

        let frame = 0

        const update = () => {
            frame = 0

            const rect = element.getBoundingClientRect()
            const total = rect.height - window.innerHeight
            const progress = total > 0 ? Math.min(Math.max(-rect.top / total, 0), 1) : 0
            const index = Math.min(STATEMENTS.length - 1, Math.floor(progress * STATEMENTS.length))

            if (bar.current) bar.current.style.transform = `scaleX(${progress})`

            setActive(index)
            setStarted(rect.top < window.innerHeight * 0.55)
        }

        const onScroll = () => {
            if (!frame) frame = window.requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', onScroll, { passive: true })

        return () => {
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onScroll)
            if (frame) window.cancelAnimationFrame(frame)
        }
    }, [])

    return (
        <section id='philosophy' ref={section} aria-labelledby='philosophy-title' className='philosophy relative h-[330svh]'>
            <h2 id='philosophy-title' className='sr-only'>Philosophy</h2>

            <ul className='sr-only'>
                {STATEMENTS.map(statement => (
                    <li key={statement.text}>{statement.text}</li>
                ))}
            </ul>

            <div className='sticky top-0 flex h-svh items-center overflow-hidden'>
                <div
                    aria-hidden='true'
                    className='pointer-events-none absolute inset-0 bg-[radial-gradient(50%_45%_at_18%_60%,rgb(201_171_124/0.07),transparent_70%)]'
                />

                <div aria-hidden='true' className='shell relative w-full'>
                    <div className='flex items-center justify-between'>
                        <p className='caps flex items-center gap-4 text-brass'>
                            <span>La philosophie</span>
                        </p>

                        <p className='caps tabular-nums text-ivory/45'>
                            0{active + 1} / 0{STATEMENTS.length}
                        </p>
                    </div>

                    <div className='mt-12 grid md:mt-16'>
                        {STATEMENTS.map((statement, index) => (
                            <p
                                key={statement.text}
                                data-state={started && index === active ? 'on' : 'off'}
                                className='philosophy-statement font-heading text-[clamp(2.4rem,8.2vw,8rem)] leading-[0.96] tracking-[-0.035em] [grid-area:1/1] [&_em]:text-brass-bright'
                            >
                                {statement.lines.map((line, lineIndex) => (
                                    <span key={lineIndex} className='line-mask'>
                                        <span className='line-inner' style={{ '--i': lineIndex } as CSSProperties}>
                                            {line}
                                        </span>
                                    </span>
                                ))}
                            </p>
                        ))}
                    </div>
                </div>

                <div aria-hidden='true' className='absolute inset-x-0 bottom-0 h-px bg-ivory/10'>
                    <span
                        ref={bar}
                        className='block h-full origin-left bg-linear-to-r from-brass-deep via-brass to-brass-bright'
                        style={{ transform: 'scaleX(0)' }}
                    />
                </div>
            </div>
        </section>
    )
}

export default Philosophy
